import { useState } from 'react';
import { toast } from 'sonner';
import { useTopup, useTransactions, useWallet } from '@/api/ads/hooks';
import { extractErrorMessage } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Stat } from '@/components/ui/stat';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, Th, Td, EmptyRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { npr, fmtDateTime } from '@/lib/format';

export function WalletPage() {
  const wallet = useWallet();
  const txns = useTransactions();
  const topup = useTopup();
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = Number(amount);
    if (!value || value <= 0) return toast.error('Enter a valid amount.');
    try {
      await topup.mutateAsync({ amount: value, reference });
      toast.success('Wallet topped up.');
      setAmount('');
      setReference('');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <>
      <PageHeader title="Wallet" description="Prepay for ads. Bookings hold funds on submit and charge on approval." />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label="Balance" value={wallet.isLoading ? '—' : npr(wallet.data?.balance)} />
        <Stat label="Held (in review)" value={wallet.isLoading ? '—' : npr(wallet.data?.held)} />
        <Stat label="Available" value={wallet.isLoading ? '—' : npr(wallet.data?.available)} />
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1.6fr]">
        <Card className="h-fit">
          <CardHeader title="Top up" description="Add funds to your ads wallet." />
          <CardBody>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Amount (NPR)</Label>
                <Input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="10000" />
              </div>
              <div>
                <Label>Payment reference (optional)</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Bank/eSewa txn id" />
              </div>
              <Button type="submit" className="w-full" loading={topup.isPending}>
                Add funds
              </Button>
              <p className="text-xs text-muted-foreground">
                Top-ups are recorded to your ledger. Connect a payment gateway to auto-verify references.
              </p>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Transactions" />
          <CardBody className="p-0">
            {txns.isLoading ? (
              <div className="p-6">
                <Spinner label="Loading ledger…" />
              </div>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Type</Th>
                    <Th>Amount</Th>
                    <Th>Balance</Th>
                    <Th>Note</Th>
                    <Th>Date</Th>
                  </tr>
                </thead>
                <tbody>
                  {(txns.data?.results.length ?? 0) === 0 && <EmptyRow colSpan={5}>No transactions yet.</EmptyRow>}
                  {txns.data?.results.map((t) => (
                    <tr key={t.id}>
                      <Td>
                        <Badge>{t.txn_type_display}</Badge>
                      </Td>
                      <Td>{npr(t.amount)}</Td>
                      <Td className="text-muted-foreground">{npr(t.balance_after)}</Td>
                      <Td className="text-muted-foreground">{t.note || '—'}</Td>
                      <Td className="text-muted-foreground">{fmtDateTime(t.created_at)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
